import React, { useState, useEffect, useRef } from 'react';
import { Github, ExternalLink, Play, Menu, X, ArrowUpRight, Music, Scan, Camera, Palette, Gamepad2 } from 'lucide-react';

const useScrollY = () => {
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => { setScrollY(window.scrollY); ticking = false; });
                ticking = true;
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    return scrollY;
};

const useInView = (threshold = 0.15) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

const Reveal = ({ children, delay = 0, className = '' }) => {
    const [ref, inView] = useInView();
    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
            }}
        >
            {children}
        </div>
    );
};

const SERVICES = [
    {
        num: '01',
        title: 'Desarrollo\nde Apps',
        desc: 'Aplicaciones móviles multiplataforma con arquitectura moderna, soporte offline y rendimiento nativo.',
        tags: ['Java', 'React Native', 'SQLite', 'JavaScript'],
        accent: '#c8f542',
    },
    {
        num: '02',
        title: 'Modelado\n3D',
        desc: 'Modelos y entornos de alta fidelidad para juegos, visualización arquitectónica y experiencias digitales.',
        tags: ['Blender', 'Rhinoceros', 'Grasshopper'],
        accent: '#c8f542',
    },
    {
        num: '03',
        title: 'Animación\n3D',
        desc: 'Animaciones arquitectónicas, rigging de personajes y gráficos en movimiento para publicidad y entretenimiento.',
        tags: ['Blender', 'After Effects', 'Unreal Engine'],
        accent: '#c8f542',
    },
];

const PROJECTS = [
    {
        id: '01',
        title: 'Himnario App',
        category: 'Mobile Application',
        desc: 'Aplicación himnario moderna con búsqueda avanzada, soporte offline y tipografía optimizada para lectura. Multiplataforma con React Native.',
        tags: ['React Native', 'SQLite', 'Offline-first'],
        icon: Music,
    },
    {
        id: '02',
        title: 'S.C.A.NNER',
        category: 'Computer Vision',
        desc: 'Aplicación de escaneo y procesamiento de documentos con reconocimiento de texto impulsado por IA, sincronización en la nube y procesamiento por lotes.',
        tags: ['Computer Vision', 'OCR', 'Cloud Sync'],
        icon: Scan,
    },
    {
        id: '03',
        title: 'Motion Graphics Reel',
        category: '3D Animation',
        desc: 'Colección de animaciones 3D y motion graphics para proyectos comerciales, incluyendo visualización de productos y brand storytelling.',
        tags: ['Motion Design', 'Product Viz', 'Branding'],
        icon: Camera,
    },
    {
        id: '04',
        title: 'Digital Art Gallery',
        category: 'Digital Art',
        desc: 'Arte digital creativo y diseños conceptuales con entornos 3D, modelado de personajes y composiciones abstractas.',
        tags: ['Concept Art', 'Environment', 'Character'],
        icon: Palette,
    },
    {
        id: '05',
        title: 'Games Showcase',
        category: 'Interactive',
        desc: 'Juegos web interactivos diseñados para diversión y engagement, con controles intuitivos y visuales vibrantes.',
        tags: ['React', 'JavaScript', 'WebGL'],
        icon: Gamepad2,
    },
];

const STACK = ['React Native', 'JavaScript', 'Blender', 'After Effects', 'SQLite', 'Grasshopper', 'Unreal Engine', 'Rhinoceros'];

export default function Main() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [hoveredProject, setHoveredProject] = useState(null);
    const scrollY = useScrollY();

    useEffect(() => { setTimeout(() => setMounted(true), 80); }, []);

    const navBg = scrollY > 60 ? 'rgba(8,8,8,0.97)' : 'transparent';

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@300;400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --accent: #818cf8;
          --accent-blue: #60a5fa;
          --accent-purple: #a78bfa;
          --accent-pink: #f472b6;
          --white: #f5f4ef;
          --gray: #8a8a8a;
          --gray-dim: #2a2a38;
          --bg: #0d0d12;
          --bg-card: #13131c;
          --serif: 'DM Serif Display', Georgia, serif;
          --mono: 'DM Mono', 'Courier New', monospace;
        }

        body { background: var(--bg); color: var(--white); font-family: var(--mono); }

        ::selection { background: var(--lime); color: var(--bg); }

        .font-serif { font-family: var(--serif); }
        .font-mono { font-family: var(--mono); }

        /* Nav */
        .nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          transition: background 0.4s ease, border-color 0.4s ease;
          border-bottom: 1px solid transparent;
        }
        .nav.scrolled { border-color: var(--gray-dim); }
        .nav-inner {
          max-width: 1400px; margin: 0 auto; padding: 0 40px;
          height: 68px; display: flex; align-items: center; justify-content: space-between;
        }
        .nav-logo { font-family: var(--serif); font-size: 1.2rem; letter-spacing: -0.01em; }
        .nav-links { display: flex; gap: 36px; align-items: center; }
        .nav-link {
          font-family: var(--mono); font-size: 0.72rem; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--gray); text-decoration: none;
          transition: color 0.2s;
        }
        .nav-link:hover { color: var(--white); }
        .nav-cta {
          font-family: var(--mono); font-size: 0.72rem; letter-spacing: 0.12em;
          text-transform: uppercase; background: linear-gradient(90deg, var(--accent-blue), var(--accent-purple)); color: var(--white);
          padding: 8px 18px; border-radius: 2px; text-decoration: none;
          font-weight: 500; transition: opacity 0.2s;
        }
        .nav-cta:hover { opacity: 0.85; }

        /* Mobile menu */
        .mobile-btn { background: none; border: none; cursor: pointer; color: var(--white); display: none; }
        .mobile-drawer {
          position: fixed; inset: 0; z-index: 99; background: var(--bg);
          display: flex; flex-direction: column; padding: 100px 40px 40px;
          gap: 12px; transform: translateX(100%); transition: transform 0.4s cubic-bezier(0.16,1,0.3,1);
        }
        .mobile-drawer.open { transform: translateX(0); }
        .mobile-link {
          font-family: var(--serif); font-size: 2.8rem; color: var(--white);
          text-decoration: none; line-height: 1.1; transition: color 0.2s;
        }
        .mobile-link:hover { color: var(--lime); }

        /* Hero */
        .hero {
          min-height: 100vh; display: flex; flex-direction: column;
          justify-content: flex-end; padding: 0 40px 80px;
          max-width: 1400px; margin: 0 auto; position: relative;
        }
        .hero-eyebrow {
          font-family: var(--mono); font-size: 0.72rem; letter-spacing: 0.15em;
          text-transform: uppercase; color: var(--accent-blue); margin-bottom: 24px;
          display: flex; align-items: center; gap: 12px;
        }
        .hero-eyebrow::before {
          content: ''; display: block; width: 32px; height: 1px; background: var(--accent-blue);
        }
        .hero-title {
          font-family: var(--serif); font-size: clamp(3.5rem, 8vw, 9rem);
          line-height: 0.92; letter-spacing: -0.02em;
          background: linear-gradient(135deg, var(--accent-blue) 0%, var(--accent-purple) 50%, var(--accent-pink) 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
          margin-bottom: 48px;
        }
        .hero-title em { font-style: italic; }
        .hero-bottom {
          display: flex; align-items: flex-end; justify-content: space-between; gap: 40px;
        }
        .hero-desc {
          font-family: var(--mono); font-size: 0.85rem; color: var(--gray);
          max-width: 340px; line-height: 1.7; letter-spacing: 0.02em;
        }
        .hero-actions { display: flex; gap: 16px; align-items: center; flex-shrink: 0; }
        .btn-primary {
          font-family: var(--mono); font-size: 0.75rem; letter-spacing: 0.1em;
          text-transform: uppercase; background: linear-gradient(90deg, var(--accent-blue), var(--accent-purple)); color: var(--white);
          padding: 14px 28px; border: none; cursor: pointer; font-weight: 500;
          transition: opacity 0.2s; border-radius: 2px; text-decoration: none; display: inline-block;
        }
        .btn-primary:hover { opacity: 0.85; }
        .btn-outline {
          font-family: var(--mono); font-size: 0.75rem; letter-spacing: 0.1em;
          text-transform: uppercase; background: transparent; color: var(--white);
          padding: 13px 28px; border: 1px solid var(--gray-dim); cursor: pointer;
          transition: border-color 0.2s, color 0.2s; border-radius: 2px;
          text-decoration: none; display: inline-flex; align-items: center; gap: 8px;
        }
        .btn-outline:hover { border-color: var(--white); }

        /* Marquee */
        .marquee-wrap {
          border-top: 1px solid var(--gray-dim); border-bottom: 1px solid var(--gray-dim);
          overflow: hidden; padding: 18px 0; background: var(--bg-card);
        }
        .marquee-track {
          display: flex; gap: 0; white-space: nowrap;
          animation: marquee 22s linear infinite;
        }
        .marquee-item {
          font-family: var(--serif); font-size: 1.1rem; font-style: italic;
          color: var(--gray); padding: 0 48px; display: flex; align-items: center; gap: 48px;
        }
        .marquee-dot { width: 5px; height: 5px; background: var(--lime); border-radius: 50%; flex-shrink: 0; }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

        /* Section */
        .section { max-width: 1400px; margin: 0 auto; padding: 120px 40px; }
        .section-header { display: flex; align-items: baseline; gap: 20px; margin-bottom: 80px; }
        .section-num {
          font-family: var(--mono); font-size: 0.7rem; letter-spacing: 0.15em;
          color: var(--accent-purple); text-transform: uppercase;
        }
        .section-title {
          font-family: var(--serif); font-size: clamp(2rem, 4vw, 3.5rem);
          line-height: 1; letter-spacing: -0.02em; color: var(--white);
        }
        .section-title em { font-style: italic; color: var(--gray); }

        /* Services */
        .services-grid {
          display: grid; grid-template-columns: repeat(3, 1fr);
          border-top: 1px solid var(--gray-dim);
        }
        .service-card {
          padding: 48px 40px; border-right: 1px solid var(--gray-dim);
          position: relative; overflow: hidden;
          transition: background 0.3s;
        }
        .service-card:last-child { border-right: none; }
        .service-card:hover { background: var(--bg-card); }
        .service-card::after {
          content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, var(--accent-blue), var(--accent-purple), var(--accent-pink));
          transform: scaleX(0); transform-origin: left;
          transition: transform 0.4s cubic-bezier(0.16,1,0.3,1);
        }
        .service-card:hover::after { transform: scaleX(1); }
        .service-num {
          font-family: var(--mono); font-size: 0.68rem; letter-spacing: 0.15em;
          color: var(--accent-purple); text-transform: uppercase; margin-bottom: 32px;
        }
        .service-title {
          font-family: var(--serif); font-size: 1.9rem; line-height: 1.05;
          letter-spacing: -0.02em; color: var(--white); margin-bottom: 24px;
          white-space: pre-line;
        }
        .service-desc {
          font-family: var(--mono); font-size: 0.78rem; color: var(--gray);
          line-height: 1.75; margin-bottom: 32px; letter-spacing: 0.01em;
        }
        .tags-wrap { display: flex; flex-wrap: wrap; gap: 8px; }
        .tag {
          font-family: var(--mono); font-size: 0.65rem; letter-spacing: 0.1em;
          text-transform: uppercase; padding: 5px 12px; border: 1px solid var(--gray-dim);
          color: var(--gray); border-radius: 2px; transition: border-color 0.2s, color 0.2s;
        }
        .service-card:hover .tag { border-color: var(--gray); color: var(--white); }

        /* Projects */
        .projects-list { border-top: 1px solid var(--gray-dim); }
        .project-row {
          display: grid; grid-template-columns: 64px 1fr auto;
          align-items: center; gap: 32px;
          padding: 36px 0; border-bottom: 1px solid var(--gray-dim);
          cursor: pointer; transition: background 0.25s; position: relative;
          text-decoration: none; color: inherit;
        }
        .project-row::before {
          content: ''; position: absolute; inset: 0; background: var(--bg-card);
          opacity: 0; transition: opacity 0.25s; pointer-events: none;
          border-radius: 4px;
        }
        .project-row:hover::before { opacity: 1; }
        .project-num {
          font-family: var(--mono); font-size: 0.68rem; letter-spacing: 0.12em;
          color: var(--gray); text-align: center; position: relative; z-index: 1;
        }
        .project-info { position: relative; z-index: 1; }
        .project-category {
          font-family: var(--mono); font-size: 0.65rem; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--accent-blue); margin-bottom: 6px;
        }
        .project-title {
          font-family: var(--serif); font-size: clamp(1.4rem, 2.5vw, 2rem);
          letter-spacing: -0.02em; line-height: 1.1; color: var(--white);
          transition: color 0.2s;
        }
        .project-row:hover .project-title { color: var(--white); }
        .project-desc {
          font-family: var(--mono); font-size: 0.75rem; color: var(--gray);
          margin-top: 8px; line-height: 1.65; max-width: 500px; letter-spacing: 0.01em;
        }
        .project-tags { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
        .project-arrow {
          position: relative; z-index: 1; color: var(--gray);
          transform: rotate(0deg); transition: color 0.2s, transform 0.3s;
        }
        .project-row:hover .project-arrow { color: var(--accent-purple); transform: rotate(0deg) scale(1.15); }

        /* About */
        .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
        .about-headline {
          font-family: var(--serif); font-size: clamp(2.2rem, 3.5vw, 3.2rem);
          line-height: 1.05; letter-spacing: -0.02em; color: var(--white);
        }
        .about-headline em { font-style: italic; color: var(--gray); }
        .about-body {
          font-family: var(--mono); font-size: 0.82rem; color: var(--gray);
          line-height: 1.85; letter-spacing: 0.01em;
        }
        .about-body p + p { margin-top: 20px; }
        .roles-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 2px; margin-top: 48px; }
        .role-card {
          background: var(--bg-card); padding: 24px 20px; border: 1px solid var(--gray-dim);
        }
        .role-label {
          font-family: var(--mono); font-size: 0.65rem; letter-spacing: 0.15em;
          text-transform: uppercase; color: var(--accent-purple); margin-bottom: 8px;
        }
        .role-title {
          font-family: var(--serif); font-size: 1.1rem; color: var(--white);
        }

        /* Stack */
        .stack-grid {
          display: grid; grid-template-columns: repeat(4, 1fr);
          border-top: 1px solid var(--gray-dim); border-left: 1px solid var(--gray-dim);
        }
        .stack-item {
          padding: 32px 28px; border-right: 1px solid var(--gray-dim);
          border-bottom: 1px solid var(--gray-dim);
          font-family: var(--mono); font-size: 0.8rem; color: var(--gray);
          letter-spacing: 0.05em; transition: background 0.25s, color 0.25s;
          display: flex; align-items: center; gap: 12px;
        }
        .stack-item::before {
          content: ''; width: 6px; height: 6px; border-radius: 50%;
          background: var(--gray-dim); flex-shrink: 0; transition: background 0.25s;
        }
        .stack-item:hover { background: var(--bg-card); color: var(--white); }
        .stack-item:hover::before { background: var(--accent-blue); }

        /* Footer */
        .footer {
          border-top: 1px solid var(--gray-dim);
          max-width: 1400px; margin: 0 auto; padding: 60px 40px;
          display: flex; justify-content: space-between; align-items: flex-end;
        }
        .footer-brand { font-family: var(--serif); font-size: 1.6rem; letter-spacing: -0.02em; }
        .footer-copy {
          font-family: var(--mono); font-size: 0.68rem; letter-spacing: 0.1em;
          color: var(--gray); text-transform: uppercase; margin-top: 8px;
        }
        .footer-links { display: flex; gap: 24px; align-items: center; }
        .footer-link {
          font-family: var(--mono); font-size: 0.68rem; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--gray); text-decoration: none;
          transition: color 0.2s;
        }
        .footer-link:hover { color: var(--accent-blue); }

        /* Hero entrance */
        .hero-enter { transition: opacity 1.1s cubic-bezier(0.16,1,0.3,1), transform 1.1s cubic-bezier(0.16,1,0.3,1); }

        /* Noise overlay */
        .noise {
          position: fixed; inset: 0; pointer-events: none; z-index: 200; opacity: 0.025;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
        }

        /* Responsive */
        @media (max-width: 900px) {
          .nav-links { display: none; }
          .mobile-btn { display: flex; }
          .hero { padding: 0 24px 60px; }
          .hero-bottom { flex-direction: column; align-items: flex-start; gap: 28px; }
          .services-grid { grid-template-columns: 1fr; }
          .service-card { border-right: none; border-bottom: 1px solid var(--gray-dim); }
          .service-card:last-child { border-bottom: none; }
          .about-grid { grid-template-columns: 1fr; gap: 40px; }
          .roles-grid { grid-template-columns: 1fr; }
          .stack-grid { grid-template-columns: repeat(2, 1fr); }
          .footer { flex-direction: column; gap: 32px; align-items: flex-start; }
          .project-row { grid-template-columns: 44px 1fr 24px; gap: 16px; }
          .section { padding: 80px 24px; }
          .nav-inner { padding: 0 24px; }
        }
        @media (max-width: 500px) {
          .hero-title { font-size: 3rem; }
          .stack-grid { grid-template-columns: 1fr; }
        }
      `}</style>

            {/* Noise texture */}
            <div className="noise" />

            {/* Nav */}
            <nav className={`nav${scrollY > 60 ? ' scrolled' : ''}`} style={{ background: navBg }}>
                <div className="nav-inner">
                    <span className="nav-logo font-serif">Goland.dev</span>
                    <div className="nav-links">
                        <a href="#services" className="nav-link">Servicios</a>
                        <a href="#projects" className="nav-link">Proyectos</a>
                        <a href="#about" className="nav-link">Nosotros</a>
                        <a href="#contact" className="nav-cta">Contacto</a>
                    </div>
                    <button className="mobile-btn" onClick={() => setMenuOpen(o => !o)}>
                        {menuOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </nav>

            {/* Mobile drawer */}
            <div className={`mobile-drawer${menuOpen ? ' open' : ''}`}>
                {['Servicios', 'Proyectos', 'Nosotros', 'Contacto'].map(l => (
                    <a key={l} href={`#${l.toLowerCase()}`} className="mobile-link" onClick={() => setMenuOpen(false)}>{l}</a>
                ))}
            </div>

            {/* Hero */}
            <section style={{ background: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
                {/* Subtle grid bg */}
                <div style={{
                    position: 'absolute', inset: 0, opacity: 0.04,
                    backgroundImage: 'linear-gradient(var(--gray-dim) 1px, transparent 1px), linear-gradient(90deg, var(--gray-dim) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }} />
                {/* Lime glow */}
                <div style={{
                    position: 'absolute', bottom: '-10%', left: '30%',
                    width: '60vw', height: '60vw', maxWidth: 700, maxHeight: 700,
                    background: 'radial-gradient(ellipse, rgba(200,245,66,0.06) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />

                <div className="hero">
                    <div
                        className="hero-enter"
                        style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(24px)' }}
                    >
                        <div className="hero-eyebrow">
                            Studio digital — Medellín, Colombia
                        </div>
                    </div>
                    <div
                        className="hero-enter"
                        style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(32px)', transitionDelay: '0.15s' }}
                    >
                        <h1 className="hero-title font-serif">
                            Código,<br />
                            <em>forma</em><br />
                            y movimiento.
                        </h1>
                    </div>
                    <div
                        className="hero-enter"
                        style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(24px)', transitionDelay: '0.3s' }}
                    >
                        <div className="hero-bottom">
                            <p className="hero-desc">
                                Desarrollo de software y creación de contenido digital. Programación, modelado 3D y animación para proyectos que necesitan ir más lejos.
                            </p>
                            <div className="hero-actions">
                                <a href="#projects" className="btn-primary">Ver portafolio</a>
                                <a href="#services" className="btn-outline">
                                    <Play size={13} style={{ flexShrink: 0 }} />
                                    Ver reel
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Marquee */}
            <div className="marquee-wrap">
                <div className="marquee-track">
                    {[...Array(2)].map((_, i) => (
                        ['React Native', 'Blender', 'After Effects', 'Unreal Engine', 'Rhinoceros', 'Grasshopper', 'JavaScript', 'SQLite', 'Motion Design'].map((item, j) => (
                            <span key={`${i}-${j}`} className="marquee-item">
                                {item}
                                <span className="marquee-dot" />
                            </span>
                        ))
                    ))}
                </div>
            </div>

            {/* Services */}
            <div id="services">
                <div className="section">
                    <Reveal>
                        <div className="section-header">
                            <span className="section-num">— Servicios</span>
                            <h2 className="section-title font-serif">Lo que <em>hacemos</em></h2>
                        </div>
                    </Reveal>
                    <div className="services-grid">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.num} delay={i * 0.1}>
                                <div className="service-card">
                                    <div className="service-num">{s.num}</div>
                                    <h3 className="service-title font-serif">{s.title}</h3>
                                    <p className="service-desc">{s.desc}</p>
                                    <div className="tags-wrap">
                                        {s.tags.map(t => <span key={t} className="tag">{t}</span>)}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>

            {/* Projects */}
            <div id="projects" style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--gray-dim)', borderBottom: '1px solid var(--gray-dim)' }}>
                <div className="section">
                    <Reveal>
                        <div className="section-header">
                            <span className="section-num">— Proyectos</span>
                            <h2 className="section-title font-serif">Trabajo <em>selecto</em></h2>
                        </div>
                    </Reveal>
                    <div className="projects-list">
                        {PROJECTS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <Reveal key={p.id} delay={i * 0.07}>
                                    <a href="#" className="project-row">
                                        <span className="project-num">{p.id}</span>
                                        <div className="project-info">
                                            <div className="project-category">{p.category}</div>
                                            <div className="project-title font-serif">{p.title}</div>
                                            <p className="project-desc">{p.desc}</p>
                                            <div className="project-tags">
                                                {p.tags.map(t => <span key={t} className="tag" style={{ fontSize: '0.6rem' }}>{t}</span>)}
                                            </div>
                                        </div>
                                        <ArrowUpRight size={20} className="project-arrow" />
                                    </a>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* About */}
            <div id="about">
                <div className="section">
                    <Reveal>
                        <div className="section-header">
                            <span className="section-num">— Nosotros</span>
                            <h2 className="section-title font-serif">El <em>estudio</em></h2>
                        </div>
                    </Reveal>
                    <div className="about-grid">
                        <Reveal>
                            <h3 className="about-headline font-serif">
                                Hacemos que las ideas <em>cobren forma</em> en cualquier dimensión.
                            </h3>
                            <div className="roles-grid">
                                {[
                                    { label: 'Rol', title: 'Developer' },
                                    { label: 'Rol', title: '3D Artist' },
                                    { label: 'Rol', title: 'Animator' },
                                ].map((r, i) => (
                                    <div key={i} className="role-card">
                                        <div className="role-label">{r.label}</div>
                                        <div className="role-title font-serif">{r.title}</div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="about-body">
                                <p>Somos un estudio digital que une código y creatividad. Nuestro camino va desde el desarrollo de aplicaciones móviles intuitivas hasta la creación de mundos 3D inmersivos y animaciones con propósito.</p>
                                <p>Ya sea construyendo una app React Native con capacidades offline, modelando entornos 3D detallados, o creando motion graphics que enganchan — llevamos ideas a la vida a través de múltiples dimensiones digitales.</p>
                                <p>Con base en Medellín, Colombia, trabajamos con clientes que exigen excelencia técnica y visión creativa al mismo tiempo.</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>

            {/* Stack */}
            <div style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--gray-dim)', borderBottom: '1px solid var(--gray-dim)' }}>
                <div className="section">
                    <Reveal>
                        <div className="section-header">
                            <span className="section-num">— Stack</span>
                            <h2 className="section-title font-serif">Herramientas <em>&amp; tecnologías</em></h2>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="stack-grid">
                            {STACK.map((s, i) => (
                                <div key={i} className="stack-item">{s}</div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </div>

            {/* CTA / Footer */}
            <div id="contact" style={{ background: 'var(--bg)' }}>
                <div className="section" style={{ paddingBottom: 0 }}>
                    <Reveal>
                        <div style={{ borderTop: '1px solid var(--gray-dim)', paddingTop: 80, marginBottom: 80, textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--lime)', marginBottom: 24 }}>
                                — Trabajemos juntos
                            </div>
                            <h2 className="font-serif" style={{ fontSize: 'clamp(2.8rem, 6vw, 6rem)', letterSpacing: '-0.02em', lineHeight: 0.95, color: 'var(--white)', marginBottom: 40 }}>
                                ¿Tienes un proyecto<br /><em style={{ color: 'var(--gray)' }}>en mente?</em>
                            </h2>
                            <a href="mailto:hola@goland.dev" className="btn-primary" style={{ fontSize: '0.8rem', padding: '16px 36px' }}>
                                Escribenos →
                            </a>
                        </div>
                    </Reveal>
                </div>
                <footer className="footer">
                    <div>
                        <div className="footer-brand font-serif">Goland.dev</div>
                        <div className="footer-copy">© 2024 — Todos los derechos reservados</div>
                    </div>
                    <div className="footer-links">
                        <a href="#" className="footer-link" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Github size={14} /> Github
                        </a>
                        <a href="#" className="footer-link" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ExternalLink size={14} /> Portafolio
                        </a>
                    </div>
                </footer>
            </div>
        </>
    );
}